import { useMemo, useState } from 'react';
import { api, formatTime, mutate } from '../api';
import { useLoad } from '../hooks';
import { useIdentity } from '../context';
import { ConfirmButton, Empty, ErrorPanel, Loading, Modal, PageHeader, StatCard, StatusBadge } from '../components/Common';

type AdminForm = {
  email: string;
  displayName: string;
  role: 'viewer' | 'operator' | 'license_admin' | 'super_admin';
  status: 'active' | 'disabled';
};

const roleLabels: Record<string, string> = {
  super_admin: '超级管理员',
  license_admin: '授权管理员',
  operator: '操作员',
  viewer: '只读查看'
};

const blankForm: AdminForm = { email: '', displayName: '', role: 'viewer', status: 'active' };

export function AdministratorsPage() {
  const identity = useIdentity();
  const state = useLoad(() => api<any>('/administrators'), []);
  const [form, setForm] = useState<AdminForm | null>(null);
  const [error, setError] = useState('');
  const items = state.data?.items || [];
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((row: any) => row.status === 'active').length,
    disabled: items.filter((row: any) => row.status === 'disabled').length,
    superAdmins: items.filter((row: any) => row.role === 'super_admin' && row.status === 'active').length
  }), [items]);

  async function save() {
    if (!form) return;
    setError('');
    try {
      await mutate(`/administrators/${encodeURIComponent(form.email)}`, 'PUT', {
        displayName: form.displayName,
        role: form.role,
        status: form.status
      }, crypto.randomUUID());
      setForm(null);
      await state.reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  async function disable(email: string) {
    await mutate(`/administrators/${encodeURIComponent(email)}/disable`, 'POST', {}, crypto.randomUUID());
    await state.reload();
  }

  async function restore(row: any) {
    await mutate(`/administrators/${encodeURIComponent(row.email)}`, 'PUT', {
      displayName: row.displayName,
      role: row.role,
      status: 'active'
    }, crypto.randomUUID());
    await state.reload();
  }

  async function remove(email: string) {
    const confirmation = window.prompt(`输入邮箱 ${email} 确认永久删除`);
    if (confirmation !== email) return;
    await mutate(`/administrators/${encodeURIComponent(email)}`, 'DELETE', { confirmation }, crypto.randomUUID());
    await state.reload();
  }

  return <>
    <PageHeader title="管理员" description="管理后台登录账号、角色权限和账号状态" actions={<button onClick={() => setForm(blankForm)}>添加管理员</button>} />
    <section className="stats-grid compact">
      <StatCard label="管理员总数" value={stats.total} detail="包含停用账号" />
      <StatCard label="启用中" value={stats.active} detail="可以登录后台" />
      <StatCard label="已停用" value={stats.disabled} detail="保留审计记录" />
      <StatCard label="超级管理员" value={stats.superAdmins} detail="至少保留 1 个" />
    </section>
    {state.loading ? <Loading /> : state.error ? <ErrorPanel message={state.error} onRetry={state.reload} /> : <div className="panel table-wrap">
      <table><thead><tr><th>管理员</th><th>角色</th><th>状态</th><th>最后登录</th><th>创建者</th><th className="actions-col">操作</th></tr></thead>
        <tbody>{items.map((row: any) => <tr key={row.email}>
          <td><strong>{row.displayName || row.email}</strong><small className="block">{row.email}</small></td>
          <td>{roleLabels[row.role] || row.role}<small className="block">{row.role}</small></td>
          <td><StatusBadge status={row.status} /></td>
          <td>{formatTime(row.lastLoginAt)}</td>
          <td>{row.createdBy || '-'}</td>
          <td><div className="row-actions">
            <button className="secondary" onClick={() => setForm({ email: row.email, displayName: row.displayName || '', role: row.role, status: row.status })}>编辑</button>
            {row.email !== identity.email && row.status === 'active' && <ConfirmButton danger message={`确认停用管理员 ${row.email}？`} onConfirm={() => disable(row.email)}>停用</ConfirmButton>}
            {row.email !== identity.email && row.status === 'disabled' && <button onClick={() => restore(row)}>恢复</button>}
            {row.email !== identity.email && <ConfirmButton danger message={`确认永久删除管理员 ${row.email}？`} onConfirm={() => remove(row.email)}>删除</ConfirmButton>}
          </div></td>
        </tr>)}</tbody></table>{!items.length && <Empty />}
    </div>}
    {form && <Modal title={form.email ? '编辑管理员' : '添加管理员'} onClose={() => setForm(null)}>
      <div className="form-grid">
        {error && <div className="notice error span-2">{error}</div>}
        <label><span>邮箱</span><input value={form.email} disabled={!!form.email && items.some((row: any) => row.email === form.email)} onChange={(event) => setForm({ ...form, email: event.target.value.trim() })} placeholder="name@example.com" /></label>
        <label><span>显示名称</span><input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} placeholder="可选" /></label>
        <label><span>角色</span><select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as AdminForm['role'] })}>
          <option value="viewer">只读查看</option><option value="operator">操作员</option><option value="license_admin">授权管理员</option><option value="super_admin">超级管理员</option>
        </select></label>
        <label><span>状态</span><select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as AdminForm['status'] })}>
          <option value="active">启用</option><option value="disabled">停用</option>
        </select></label>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={() => setForm(null)}>取消</button><button onClick={save}>保存</button></div>
    </Modal>}
  </>;
}
